import { useEffect, useState } from 'react';
import { LayersIcon, SearchIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthedFetcher } from '@/hooks/fetch';

type ResourceStatus = 'started' | 'stopped' | 'starting' | 'stopping' | 'missing';

type ResourceData = {
    name: string;
    cpu: number;
    memory: number;
    status: ResourceStatus;
};

export default function ResourcePerformanceCard() {
    const [search, setSearch] = useState('');
    const [resources, setResources] = useState<ResourceData[]>([]);
    const [loading, setLoading] = useState(true);
    const fetcher = useAuthedFetcher();

    const load = async () => {
        try {
            const data = await fetcher<{ resources: { name: string; status: ResourceStatus; ms?: number; tick?: number }[] }>('/resources');
            if (Array.isArray(data?.resources)) {
                setResources(data.resources.map((r) => ({
                    name: r.name,
                    status: r.status,
                    cpu: r.ms ?? r.tick ?? 0,
                    memory: 0,
                })));
            }
        } catch {
            // bridge offline
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 15_000);
        return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = resources.filter(r =>
        r.name.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="py-4 px-5 rounded-xl border bg-card/60 border-white/5 backdrop-blur-xl shadow-md flex flex-col h-[22rem] overflow-hidden group hover:border-white/10 transition-all duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-white/[0.04] mb-3">
                <h3 className="tracking-tight text-sm font-semibold flex items-center gap-1.5 text-slate-200">
                    <LayersIcon size={14} className="text-scPurple" />
                    Resource Impact
                </h3>
                <div className="relative w-36">
                    <input
                        type="text"
                        placeholder="Search resource..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full bg-white/[0.02] border border-white/5 rounded-md py-1 pl-6 pr-2 text-[10px] text-slate-200 placeholder:text-muted-foreground focus:outline-none focus:border-scPurple/40 focus:bg-white/[0.04] transition-all"
                    />
                    <SearchIcon size={10} className="absolute left-2 top-2 text-muted-foreground" />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/5 hover:scrollbar-thumb-white/10">
                {loading ? (
                    <div className="flex items-center justify-center h-full text-xs text-muted-foreground">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
                        {resources.length === 0 ? 'No resource data available.' : 'No matches.'}
                    </div>
                ) : (
                    <table className="w-full text-left text-xs border-collapse">
                        <thead>
                            <tr className="border-b border-white/[0.02] text-[10px] uppercase font-bold tracking-wider text-muted-foreground">
                                <th className="pb-2">Resource</th>
                                <th className="pb-2 text-right">Tick</th>
                                <th className="pb-2 text-right pr-1">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.02]">
                            {filtered.map((res) => (
                                <tr key={res.name} className="hover:bg-white/[0.01] transition-colors">
                                    <td className="py-2.5 font-semibold text-slate-200">{res.name}</td>
                                    <td className="py-2.5 text-right font-mono text-[11px] text-slate-300">
                                        {res.cpu > 0 ? `${res.cpu.toFixed(1)} ms` : '—'}
                                    </td>
                                    <td className="py-2.5 text-right pr-1">
                                        <span className={cn(
                                            "text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase",
                                            res.status === 'started'
                                                ? "bg-scGreen/5 border-scGreen/10 text-scGreen"
                                                : res.status === 'starting' || res.status === 'stopping'
                                                ? "bg-warning/5 border-warning/10 text-warning"
                                                : "bg-destructive/5 border-destructive/10 text-destructive"
                                        )}>
                                            {res.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
